/**
 * « Partager ma boutique » — what the card shows, as a pure function of data the dashboard already fetches.
 *
 * The link is offered only when the shop really is visible to the public. The rule mirrors the public side: the shop
 * exists, its tenant is `active` (the same field `app.store_is_publicly_visible()` is driven by), and at least one
 * product is `active` (the only status the public catalogue lists). Otherwise the merchant is told the ONE next step.
 *
 *   loading      something is still being fetched: show nothing yet (no flash of « create your shop »)
 *   unavailable  a read failed (e.g. a staff role without catalogue access): say nothing rather than guess
 *   no-store     no shop configured yet
 *   pending      the shop exists but the platform has not activated it
 *   suspended    the shop is suspended: not visible
 *   refused      the platform refused the application: nothing to wait for, and nothing to share
 *   no-product   active shop, no product at all
 *   unpublished  products exist but none is `active`
 *   ready        shop active and at least one active product
 */
export type ShareState =
  | 'loading'
  | 'unavailable'
  | 'no-store'
  | 'pending'
  | 'suspended'
  | 'refused'
  | 'no-product'
  | 'unpublished'
  | 'ready';

export interface ShareInputs {
  /** `undefined` while loading, `null` when the merchant has no shop yet. */
  readonly store: { readonly slug: string } | null | undefined;
  readonly storeFailed: boolean;
  /** `undefined` while the memberships load. */
  readonly tenantStatus: string | undefined;
  /** `undefined` while loading. */
  readonly products: readonly { readonly status: string }[] | undefined;
  readonly productsFailed: boolean;
}

export function resolveShareState(input: ShareInputs): ShareState {
  if (input.storeFailed || input.productsFailed) return 'unavailable';
  if (input.store === undefined) return 'loading';
  if (input.store === null) return 'no-store';
  if (input.tenantStatus === undefined) return 'loading';
  if (input.tenantStatus === 'suspended') return 'suspended';
  if (input.tenantStatus === 'refused') return 'refused';
  if (input.tenantStatus !== 'active') return 'pending';
  if (input.products === undefined) return 'loading';
  if (input.products.length === 0) return 'no-product';
  if (!input.products.some((product) => product.status === 'active')) return 'unpublished';
  return 'ready';
}
